// Utils
import { hasPermission, matchesRoles, getOrgsByFeatureFlag } from './permissions';
import { sortByOrder } from './collections';
import { get, isEmpty, isEqual } from 'lodash';

// Constants
import { TEMPLATE_URL_MAPPING, CONTROLLER_NAME_MAPPING } from 'libs/utils/constants';

const defaultAssetColumns = (fpType) => ([
    {
        'label': 'Preview',
        'component': 'AssetPreview',
    }, {
        'field': 'fp_ext_id',
        'label': 'fp_ext_id',
        'link': {
            'controllerKey': `view-${fpType}`
        }
    }, {
        'field': 'original_content_type',
        'label': 'File type',
    }
]);

export function getCrudColumns(fpType, crudOpts = {}) {
    const columns = crudOpts.columns || [];

    if (!columns.length && crudOpts.useDefaultAssetColumns) {
        return defaultAssetColumns(fpType);
    }

    return columns;
}


/**
 * Given a module or controller this method checks if it matches with the current route.
 *
 * @param {Object} currentRoute the actual route.
 * @param {Object} currentSearchParams the actual search parameters
 * @param {Object} module the module or controller on which perform the lookup
 * @param {Boolean} [perfectMatch=false] if the match must be perfect or partial
 *
 * @return {Boolean} true if the link matches with the current route, false otherwise
 */
export function matchRoute(currentRoute, currentSearchParams, module, perfectMatch = false) {
    // Set routes for this link if not set yet.
    if (!module.routes) {
        module.routes = getRoutesForModule(module);
    }

    // Look for first matching
    for (const route of module.routes) {
        // matches route and query params if the module defines those
        if (perfectMatch && currentRoute.originalPath === route &&
            (!module.searchParams || isEqual(module.searchParams, currentSearchParams))) {
            return true;
        } else if (!perfectMatch && (currentRoute.originalPath || '').indexOf(route) === 0) {
            return true;
        }
    }

    return false;
}

/**
 * Given a blueprint or controller module this method returns the first relevant route.
 *
 * @param {Object} module the module or controller on which perform the lookup
 *
 * @return {String} the first relevant route or an empty string
 */
export function getRouteForModule(module) {
    // Pick the default controller if defined or the first of the list.
    // On exploded menu, the passed module is controller
    const ctrl = getRelevantControllerForModule(module);

    // Route can be either an array or a string.
    // In former case pick the first.
    const route = Array.isArray(ctrl.route) ? ctrl.route[0] : ctrl.route;

    return route || '';
}

/**
 * Given a blueprint or controller module this method returns the first relevant controller.
 *
 * @param {Object} module the module or controller on which perform the lookup
 *
 * @return {Object} the first relevant controller or an empty object
 */
export function getRelevantControllerForModule(module) {
    // Pick the default controller if defined or the first of the list.
    // On exploded menu, the link itself could define the controller.
    let ctrl = module.controllers ? module.controllers.default : module;

    if (!ctrl) {
        ctrl = sortByOrder(module.controllers)[0];
    }

    return ctrl || {};
}

/**
 * Given a blueprint module or controller this method returns all *unsorted* routes associated to it.
 *
 * @private
 *
 * @param {Object} module the module or controller on which perform the lookup
 *
 * @return {String[]} all available link's routes
 */
function getRoutesForModule(module) {
    const routes = [];

    for (const ctrl of getControllersForModule(module)) {
        // Route can be either an array or a string.
        if (Array.isArray(ctrl.route)) {
            routes.push(...ctrl.route);
        } else {
            routes.push(ctrl.route);
        }
    }
    return routes;
}

/**
 * Given a blueprint module or controller this method returns all its controllers sorted by `order`
 *
 * @private
 *
 * @param {Object} module the module or controller on which perform the lookup
 *
 * @return {Object[]} all available module's controllers
 */
function getControllersForModule(module) {
    if (!module.controllers) {
        return [module];
    }

    const ctrls = sortByOrder(module.controllers);

    return ctrls.filter(ctrl => !ctrl.bstgMenu);
}

/**
 * Generates relative link for given module
 *
 * @param {Object} module module from blueprint
 * @param {Object} params replacement parameters for route
 * @param {Object} query query parameters
 * @returns {String} relative link
 */
export function getLinkForModule(module, params, query = {}) {
    let path = getRouteForModule(module);

    for (const param of Object.keys(params)) {
        path = path.replace(`:${param}`, params[param]);
    }

    if (!isEmpty(query)) {
        const queryString = Object.keys(query)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
            .join('&');

        path = `${path}?${queryString}`;
    }

    return path;
}

/**
 * Converts a controller to a route configuration object.
 *
 * @param {Object} module - The module containing the controller.
 * @param {string} controllerName - The name of the controller.
 * @param {Object} [meta={}] - Additional metadata to include in the route.
 *
 * @returns {Object|null} The route configuration object or null if no path is found.
 * @property {string} name - The name of the route in the format `${module.name}.${controller.name}`.
 * @property {string} path - The path of the route.
 * @property {string} component - The component name to be used for the route.
 * @property {Object} meta - Metadata for the route including module and controller details.
 */
export function controllerToRoute(module, controllerName, meta = {}) {
    const controller = module.controllers[controllerName];
    const [path] = Array.isArray(controller.route) ? controller.route : [controller.route];

    if (!path) {
        return null;
    }

    return {
        name: `${module.name}.${controllerName}`,
        path,
        component: controller.componentName || 'AngularPage',
        meta: { moduleName: module.name, controllerName, module, controller, ...meta }
    };

}

/**
 * Sanitizes the names of modules and their controllers.
 *
 * This function iterates over the provided modules and ensures that each module
 * and its controllers have appropriate titles. If a controller does not have a title,
 * it assigns the module's title or the controller's name as the title. Similarly, it
 * assigns the module's name to the module's title if it is not already set.
 *
 * @param {Object} modules - An object containing modules, where each module is an object
 *                           with a `controllers` property that is also an object.
 *
 * @private
 */
export function sanitizeNames(modules) {
    for (const [moduleName, module] of Object.entries(modules)) {
        if (module?.controllers) {
            // Sanitize modules and controllers names
            for (const [ctrlName, controller] of Object.entries(module.controllers)) {
                controller.title = controller.title || module.title || ctrlName;
            }
            module.name = moduleName;
            module.title = module.title || module.name;
        }
    }
}

/**
 * Applies permissions to the given blueprint object based on the event and user context.
 *
 * @param {Object} blueprint - The blueprint object to which permissions will be applied.
 * @param {Object} context - The context containing event and user information.
 * @param {Object} context.event - The event object containing event details.
 * @param {Object} context.user - The user object containing user details and roles.
 *
 * @returns {Object} - The modified blueprint object with applied permissions.
 *
 * @private
 */
export function applyPermissions(blueprint, { event, user }) {
    Object.values(blueprint.modules).forEach(moduleItem => {
        if (moduleItem?.controllers) {
            removeUnaccessibles(moduleItem.controllers, user, event, { name: moduleItem.name, type: moduleItem.type ?? 'module' });
        }
    });

    removeUnaccessibles(blueprint.modules, user, event);

    return blueprint;
}

/**
 * Removes inaccessible controllers or modules from the given object based on the user's access rights and event.
 * If the user does not have access to a control or module, it is deleted from the object.
 * Additionally, if the call-to-actions should not be shown, they are removed from the route options.
 *
 * @param {Object} ctrlsOrModules - The object containing controllers or modules to be filtered.
 * @param {Object} user - The user object used to determine access rights.
 * @param {Object} [event] - The event object used to determine access rights.
 * @param {{name: string, type: string}} parent - ...
 *
 * @private
 */
function removeUnaccessibles(ctrlsOrModules, user, event, parent) {
    const showCta = shouldShowCta(user, event);

    Object.keys(ctrlsOrModules).forEach(name => {
        const ctrlOrModule = ctrlsOrModules[name];

        if (!ctrlOrModule || !hasAccess({ ...ctrlOrModule, ctrlOrModuleParent: parent, name }, user, event)) {
            delete ctrlsOrModules[name];
        }

        if (!showCta && ctrlOrModule.routeOptions?.callToActions) {
            delete ctrlOrModule.routeOptions.callToActions;
        }
    });
}

/**
 * Determines whether the Call to Action (CTA) should be shown for a given event and user.
 *
 * @param {Object} user - The user object.
 * @param {Array<string>} user.roles - The roles assigned to the user.
 * @param {Object} [event] - The event object.
 * @param {string} [event._id] - The unique identifier of the event.
 * @param {string} [event.bs_owner_id] - The Backstage owner ID of the event.
 * @param {string} [event.ownerId] - The owner ID of the event.
 *
 * @returns {boolean} - Returns true if the user has permission to see the CTA for the event, otherwise false.
 *
 * @private
 */
function shouldShowCta(user, event) {
    if (!event) return false;
    const permissionContext = {
        event_id: event._id,
        org_id: event.bs_owner_id || event.ownerId
    };
    return hasPermission(user.roles, 'event_actions.show_cta', permissionContext);
}

/**
 * Checks if the user has access to a specific controller or module for a given event.
 *
 * @param {Object} ctrlOrModule - The controller or module to check access for.
 * @param {Object} user - The user whose access is being checked.
 * @param {Object} [event] - The event to check access against.
 *
 * @returns {boolean} - Returns true if the user has access, otherwise false.
 *
 * @private
 */
function hasAccess(ctrlOrModule, user, event) {
    const { canAccess, roles } = getAccessAndRoles(ctrlOrModule, user, event);
    return matchesRoles(event?._id, user, roles) && canAccess;
}

/**
 * Determines the access and roles for a given controller or module based on the user and event.
 *
 * @param {Object} ctrlOrModule - The controller or module containing roles, permissions, and feature flags.
 * @param {Object} user - The user object containing roles and organizations.
 * @param {Array} user.orgs - The organizations the user belongs to.
 * @param {Array} user.roles - The roles assigned to the user.
 * @param {Object} [event] - The event object, if applicable.
 *
 * @returns {{ canAccess: boolean, roles: object[] }} An object containing the access status and roles.
 * @returns {boolean} return.canAccess - Indicates if the user has access.
 * @returns {Array} return.roles - The roles associated with the controller or module.
 *
 * @private
 */
function getAccessAndRoles(ctrlOrModule, user, event) {
    let { roles, permission, permissions } = ctrlOrModule;
    if (!roles && ctrlOrModule.hideNavUnlessUserGlobalPermission) {
        roles = [{ globalRole: ctrlOrModule.hideNavUnlessUserGlobalPermission }];
    }
    if (!roles && !permission && !permissions) {
        permissions = createDynamicPermissions(ctrlOrModule);
    }

    let canAccess = true;
    let orgs = user.orgs;
    if (event) {
        const org = orgs.find(org => org._id === event.ownerId);
        // [{}] - allows users with contextless roleDefinitions to access.
        orgs = org ? [org] : [{}];
    }

    if (ctrlOrModule.featureFlag) {
        orgs = getOrgsByFeatureFlag(orgs, ctrlOrModule.featureFlag);
        canAccess = !!orgs.length;
    }

    if (permission && canAccess) {
        canAccess = orgs.some(org => {
            const context = { org_id: org._id };
            if (event) context.event_id = event._id;

            return hasPermission(user.roles, permission, context);
        });
    }

    // Some roles depend on skipping orgs.some() iteration - eg. event_sponsor.
    // In this case .every() will return false, and lock out sponsors from accessing their accredited link.
    if (permissions && canAccess && orgs.length) {
        canAccess = permissions.every(permissionRule => {
            return orgs.some(org => {
                const context = { org_id: org._id };
                if (event) context.event_id = event._id;

                return hasPermission(user.roles, permissionRule, context);
            });
        });
    }

    return { canAccess, roles };
}

/**
 * Creates a dynamic access permission from controller or module parameters.
 *
 * @param {Object} ctrlOrModule - The controller or module containing roles, permissions, and feature flags.
 *
 * @returns {Array} The roles associated with the controller or module.
 *
 * @private
 */
function createDynamicPermissions(ctrlOrModule) {
    const { ctrlOrModuleParent } = ctrlOrModule;

    if (ctrlOrModuleParent) {
        return [
            // eg. app_bstg.global.welcome.templates
            `app_bstg.${ctrlOrModuleParent.type}.${ctrlOrModuleParent.name}.${ctrlOrModule.name}`
        ];
    }

    if (ctrlOrModule.type && ctrlOrModule.name) {
        return [
            // eg. app_bstg.global.welcome
            `app_bstg.${ctrlOrModule.type}.${ctrlOrModule.name}`
        ];
    }

    if (ctrlOrModule.name) {
        return [
            // eg. app_bstg.module.home-feed
            `app_bstg.module.${ctrlOrModule.name}`
        ];
    }

    return [
        // final fallback which shouldn't really occur
        'app_bstg.core_unknown.*'
    ];
}

/**
 * Remaps the template URLs and controller names in the given blueprint object.
 *
 * This function iterates through the modules and controllers in the blueprint object,
 * and replaces the template URLs and controller names with the corresponding values
 * from the TEMPLATE_URL_MAPPING and CONTROLLER_NAME_MAPPING objects, if they exist.
 *
 * @param {Object} blueprint - The blueprint object containing modules and controllers.
 * @param {Object} [blueprint.modules] - The modules in the blueprint.
 * @param {Object} [blueprint.modules.controllers] - The controllers in each module.
 * @param {string} [blueprint.modules.controllers.templateUrl] - The template URL of the controller.
 * @param {string} [blueprint.modules.controllers.controller] - The name of the controller.
 *
 * @private
 */
export function remapTemplatesAndControllers(blueprint = {}) {
    for (const mod of Object.values(blueprint.modules || {})) {
        for (const ctrl of Object.values(mod.controllers || {})) {
            if (TEMPLATE_URL_MAPPING[ctrl.templateUrl]) {
                ctrl.templateUrl = TEMPLATE_URL_MAPPING[ctrl.templateUrl];
            }

            if (CONTROLLER_NAME_MAPPING[ctrl.controller]) {
                ctrl.controller = CONTROLLER_NAME_MAPPING[ctrl.controller];
            }
        }
    }
}

/**
 * Filters the given modules to return only those that have controllers in their _extend property.
 *
 * @param {object} modules - The array of module objects to filter.
 *
 * @returns {Array} - An array of modules that have controllers in their _extend property.
 *
 * @private
 */
export function getAllExtensions(modules) {
    return sortByOrder(Object.values(modules)
        .filter(module => get(module, '_extend.modules'))
        .map(module => module._extend.modules));
}
